import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { connect, ResolveThunks } from "react-redux";

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { FC, useContext, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    productId: string;
    isLoading: boolean;
    errorMessage: string;
    reelFinalInventory: any[];
    unitOfMeasure: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const cartState = getCurrentCartState(state).value;
    const { isUpdatingCart } = state.context;

    return {
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        cartState,
        isAddToCartEnabled: state.context?.settings?.properties?.isAddToCartEnabled?.toLowerCase() === "true",
        isReelQuantityEditable: state.context?.settings?.properties?.isReelQuantityEditable?.toLowerCase() === "true",
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ReelModelStyles {
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    button?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    qtyContainer?: DataTableCellProps;
    addToCartContainer?: DataTableCellProps;
}

export const ReelModelStyles: ReelModelStyles = {
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    availabilityByWarehouseTableHeader: {
        css: css`
            background: #f6f6f6 !important;
            span {
                color: #000 !important;
            }
        `,
    },
    button: {
        css: css`
            width: 100%;
            border-radius: 0px 10px 10px 0px;
            background: transparent linear-gradient(180deg, #44a8f7 0%, #1e75ba 100%) 0% 0% no-repeat;
            border: none;
            text-transform: uppercase;
            font-size: ${TYPOGRAPHY.size.smaller};
            background-color: #1e75ba;
            color: ${COLORS.white};
            &:focus {
                outline: none;
            }
            &:hover {
                background-color: ${COLORS.link.base};
            }
            @media (max-width: 991px) {
                border-radius: 10px;
            }
            span {
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: #fff !important;
            }
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
            `,
            formField: css`
                margin-top: 0px;
                flex-direction: row;
                align-items: center;
                color: ${COLORS.lightBlack};
                label {
                    text-align: center;
                    padding: 9px;
                    width: 50%;
                    border-radius: 0;
                    background-color: #e1e7ed;
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                        padding: 11px 10px 10px;
                    }
                    @media (max-width: 991px) {
                        border-radius: 8px 0 0 8px;
                    }
                }
                & > div {
                    margin-top: 0 !important;
                    > input {
                        border: 0 none !important;
                        background-color: #f1f6fb !important;
                        @media (max-width: 991px) {
                            border-radius: 0 8px 8px 0 !important;
                        }
                    }
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                    }
                }
            `,
        },
    },
    availabilityByWarehouseTable: {
        css: css`
            td {
                padding-top: 15px;
                padding-bottom: 15px;
            }
        `,
    },
    addToCartContainer: {
        css: css`
            padding-left: 0 !important;
        `,
    },
    qtyContainer: {
        css: css`
            padding-right: 0 !important;
        `,
    },
};

const styles = ReelModelStyles;

const ReelModel: FC<Props> = ({
    isLoading,
    errorMessage,
    reelFinalInventory,
    unitOfMeasure,
    addToCart,
    productId,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    isAddToCartButtonDisabled,
    cartState,
    isAddToCartEnabled,
    isReelQuantityEditable,
}) => {
    const [localReelInventoryState, setLocalReelInventoryState] = useState(reelFinalInventory);
    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        if (reelFinalInventory.length) {
            setLocalReelInventoryState(
                reelFinalInventory.map(item => {
                    return {
                        ...item,
                        reelQty: parseFloat(item.reelQty),
                    };
                }),
            );
        }
    }, [reelFinalInventory]);

    const updateReelQty = (event: React.ChangeEvent<HTMLInputElement>, id: any) => {
        const updatedInventory = localReelInventoryState.map(item => {
            if (item.reelId === id) {
                return {
                    ...item,
                    reelQty: event.target.value,
                };
            }
            return item;
        });
        setLocalReelInventoryState(updatedInventory);
    };

    const addReelToCart = async (reelItem: any) => {
        const reelItemInCartTotal =
            cartState?.cartLines
                ?.filter(cartLine => cartLine.properties.reelId === reelItem.reelId)
                ?.reduce((acc, cartLine) => {
                    acc = acc + cartLine.qtyOrdered!;
                    return acc;
                }, 0) || 0;

        const maxQty = reelFinalInventory?.find(item => item?.reelId === reelItem?.reelId)?.reelQty;
        const maxQtyToAdd = Number(maxQty) - reelItemInCartTotal;

        if (parseFloat(reelItem.reelQty) > maxQtyToAdd) {
            toasterContext.addToast({
                body: `${siteMessage("qty_excedded")}`,
                messageType: "danger",
                timeoutLength: addToCartPopupTimeout,
            });

            return;
        }

        const cartLine = await addToCart({
            productId,
            qtyOrdered: parseFloat(reelItem.reelQty),
            unitOfMeasure,
            properties: {
                reelId: reelItem.reelId,
                siteId: reelItem.siteId,
                warehouseIdCustom: reelItem.warehouseId,
                reelTotalQty: `${maxQty}`,
            },
        } as any);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={false} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: addToCartPopupTimeout,
            });
        }
    };

    return (
        <StyledWrapper>
            {isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!isLoading && (
                <>
                    {errorMessage ? (
                        <div>{errorMessage}</div>
                    ) : (
                        <DataTable
                            {...styles.availabilityByWarehouseTable}
                            data-test-selector="availabilityByWarehouseModal"
                        >
                            <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                    {translate("Reel Id")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                    {translate("QTY Available")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                    {translate("UOM")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}></DataTableHeader>
                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}></DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                {localReelInventoryState.length !== 0 &&
                                    localReelInventoryState?.map((value, index) => {
                                        const qtyDisabled = reelFinalInventory[index].reelQty <= 0;

                                        const addToCartDisabled =
                                            !value.reelQty ||
                                            value.reelQty === "0" ||
                                            Number(value.reelQty) === 0 ||
                                            reelFinalInventory[index].reelQty <= 0 ||
                                            value.reelQty > Number(reelFinalInventory[index].reelQty) ||
                                            isAddToCartButtonDisabled;

                                        return (
                                            <DataTableRow key={value.reelId}>
                                                <DataTableCell>{value.reelId}</DataTableCell>
                                                <DataTableCell>{reelFinalInventory[index].reelQty}</DataTableCell>
                                                <DataTableCell>{unitOfMeasure}</DataTableCell>
                                                <DataTableCell {...styles.qtyContainer}>
                                                    <TextField
                                                        label={translate("QTY")}
                                                        type="number"
                                                        min={0}
                                                        disabled={!isReelQuantityEditable}
                                                        value={value.reelQty}
                                                        max={999999}
                                                        {...styles.quantityOrdered}
                                                        onChange={e => updateReelQty(e, value.reelId)}
                                                    />
                                                </DataTableCell>
                                                {isAddToCartEnabled && (
                                                    <DataTableCell {...styles.addToCartContainer}>
                                                        <Button
                                                            {...styles.button}
                                                            disabled={addToCartDisabled}
                                                            onClick={() => addReelToCart(value)}
                                                        >
                                                            {translate("Add to Cart")}
                                                        </Button>
                                                    </DataTableCell>
                                                )}
                                            </DataTableRow>
                                        );
                                    })}
                            </DataTableBody>
                        </DataTable>
                    )}
                    {!localReelInventoryState?.length ? (
                        <StyledWrapper
                            style={{
                                textAlign: "center",
                                padding: "10px",
                            }}
                        >
                            {siteMessage("Reel_Inventory_not_available")}
                        </StyledWrapper>
                    ) : null}
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReelModel);
